import React, { useEffect, useState } from "react";
import axios from "axios";
import { X, Tag, Clock } from "lucide-react";
import { useFlexfuelOffer } from "../hooks/useFlexfuelOffer";
import { daysLeft, countdownLabel } from "./OfferCountdown";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STORAGE_KEY = "atr_static_banner_dismissed";

const STYLES = {
  red: "bg-gradient-to-r from-[#FF3B30] to-[#FF6B30] text-white",
  dark: "bg-gradient-to-r from-neutral-900 to-neutral-700 text-white",
  gold: "bg-gradient-to-r from-yellow-500 to-amber-500 text-black",
  green: "bg-gradient-to-r from-emerald-600 to-emerald-500 text-white",
};

/**
 * Editable static banner shown at the very top of the page (above the Navbar).
 * Fully driven by the CMS `banner` key. Dismissible; re-shows after a fresh
 * message is deployed (dismissed message ID is stored in localStorage).
 */
export const StaticBanner = ({ onHeightChange, onEnabledChange, inline = false }) => {
  const [cfg, setCfg] = useState(null);
  const [visible, setVisible] = useState(false);
  const offer = useFlexfuelOffer();

  useEffect(() => {
    axios
      .get(`${API}/content/banner`)
      .then((r) => {
        const c = r.data || {};
        setCfg(c);
        onEnabledChange?.(!!c.enabled && !!c.message);
        if (!c.enabled) return;
        try {
          const dismissed = localStorage.getItem(STORAGE_KEY);
          if (dismissed === c.message) return;
        } catch {
          /* ignore */
        }
        setVisible(true);
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    onHeightChange && onHeightChange(visible);
  }, [visible, onHeightChange]);

  const dismiss = () => {
    try {
      if (cfg?.message) localStorage.setItem(STORAGE_KEY, cfg.message);
    } catch {
      /* ignore */
    }
    setVisible(false);
  };

  if (!cfg || !cfg.enabled || !visible) return null;

  const styleClass = STYLES[cfg.style] || STYLES.red;
  const days = cfg.show_countdown !== false && offer.active && offer.end_date && /flexfuel/i.test(`${cfg.message} ${cfg.cta_url}`) ? daysLeft(offer.end_date) : -1;

  const content = (
    <div className="max-w-7xl mx-auto flex items-center justify-center gap-2 sm:gap-3 w-full min-w-0 pr-5">
      <Tag size={14} className="flex-shrink-0 hidden sm:block" />
      <div className="flex items-center min-w-0">
        <span className="font-bold text-xs sm:text-sm truncate">{cfg.message}</span>
        {cfg.highlight_text && (
          <span className="ml-2 flex-shrink-0 bg-white/25 px-2 py-0.5 rounded text-xs font-bold">
            {cfg.highlight_text}
          </span>
        )}
        {days >= 0 && (
          <span
            data-testid="static-banner-countdown"
            className="ml-2 flex-shrink-0 inline-flex items-center gap-1 bg-white text-[#FF3B30] px-2 py-0.5 rounded-full text-[10px] sm:text-[11px] font-bold uppercase tracking-wide whitespace-nowrap"
          >
            <Clock size={11} />
            {countdownLabel(days, true)}
          </span>
        )}
      </div>
      {cfg.cta_text && cfg.cta_url && (
        <a
          href={cfg.cta_url}
          data-testid="static-banner-cta"
          className="hidden md:inline-flex items-center gap-1 bg-white/20 hover:bg-white/30 px-3 py-0.5 rounded text-xs font-semibold uppercase tracking-wider transition-colors"
        >
          {cfg.cta_text}
        </a>
      )}
    </div>
  );

  return (
    <div
      data-testid="static-banner"
      className={`${inline ? "relative" : "fixed top-0 left-0 right-0"} z-[70] ${styleClass} h-[28px] flex items-center px-4 text-sm`}
    >
      {content}
      <button
        onClick={dismiss}
        data-testid="static-banner-close"
        className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 hover:bg-black/20 rounded transition-colors"
        aria-label="Fermer"
      >
        <X size={14} />
      </button>
    </div>
  );
};
